import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AppContextService, AppContext } from './app-context.service';
import { RealAiApiService, AiApiRequest, AiApiResponse } from './real-ai-api.service';
import { UserService } from './user.service';

/**
 * Chat message types
 */
export type MessageType = 'user' | 'ai' | 'system' | 'error';

/**
 * Interface for a single chat message
 */
export interface ChatMessage {
  id: string;
  type: MessageType;
  content: string;
  timestamp: Date;
  context?: AppContext;
}

/**
 * Interface for suggested prompts
 */
export interface SuggestedPrompt {
  text: string;
  icon?: string;
  task?: string; // Task identifier sent to backend (e.g., 'careplan', 'vitals', 'medications')
}
@Injectable({
  providedIn: 'root'
})
export class AiChatService {
  private messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  public messages$: Observable<ChatMessage[]> = this.messagesSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$: Observable<boolean> = this.loadingSubject.asObservable();

  private suggestedPromptsSubject = new BehaviorSubject<SuggestedPrompt[]>([]);
  public suggestedPrompts$: Observable<SuggestedPrompt[]> = this.suggestedPromptsSubject.asObservable();

  private currentContext: AppContext;
  private readonly MAX_MESSAGES = 50;
  public responseFronGet: any;

  constructor(
    private appContextService: AppContextService,
    private realAiApiService: RealAiApiService,
    private userService: UserService
  ) {
    console.log('🤖 AI Chat Service initialized with REAL API');

    // Subscribe to context changes
    this.appContextService.context$.subscribe(context => {
      this.currentContext = context;
      this.updateSuggestedPrompts(context);
    });


    // Get initial context
    this.currentContext = this.appContextService.getContext();
    this.updateSuggestedPrompts(this.currentContext);
  }

  public sendMessage(messageText: string, taskId?: string): void {
    if (!messageText || !messageText.trim()) {
      return;
    }

    // Add user message to chat
    const userMessage: ChatMessage = {
      id: this.generateMessageId(),
      type: 'user',
      content: messageText.trim(),
      timestamp: new Date(),
      context: this.currentContext
    };
    /*
            let a = {
              patient_id: "P0001",
              session_id: "S1",
              chat_id: "C1",
              message: "Hi, I have diabetes. What should I focus on daily?"
            }*/
    this.addMessage(userMessage);

    // Set loading state
    this.loadingSubject.next(true);

    // Build AI API request with context
    const request: AiApiRequest = {
      message: messageText.trim(),
      user_id: this.userService.current_user?.id,
      practice_id: this.userService.current_user?.profileable_id,
      screen: this.currentContext.screen_name,
      active_tab: this.currentContext.active_tab,
      patient_id: this.currentContext.patient_id?.toString(),
      r_patient_id: this.currentContext.patient_id,
      program_id: this.currentContext.program_id,
      timestamp: new Date().toISOString(),
      session_id: 'S1',
      chat_id: 'C1'
    };

    // Include task identifier if provided (from suggested prompts)
    if (taskId) {
      request.task = taskId;
      console.log('📋 Sending message with task identifier:', taskId);
    }




    console.log('AI Request with context:', request);

    // Call Real AI API
    this.realAiApiService.sendMessage(request).subscribe(
      (response) => {
        const aiMessage: ChatMessage = {
          id: response['chat_id'],
          type: 'ai',
          content: response['answer'],
          timestamp: new Date(),
          context: this.currentContext
        };
        this.addMessage(aiMessage);
        this.loadingSubject.next(false);
      },
      (error) => {
        // Add error message to chat
        const errorMessage: ChatMessage = {
          id: this.generateMessageId(),
          type: 'error',
          content: 'I apologize, but I\'m having trouble connecting right now. Please try again in a moment.',
          timestamp: new Date()
        };
        this.addMessage(errorMessage);
        this.loadingSubject.next(false);
        console.error('AI API Error:', error);
      }
    );
  }


  getCurrentMessage() {
    this.realAiApiService.getCurrentUserChats().then(resp => {
      const aiMessage: ChatMessage = {
        id: this.generateMessageId(),
        type: 'ai',
        content: resp,
        timestamp: new Date(resp.timestamp),
        context: this.currentContext
      };
      this.responseFronGet = resp

    }).catch(error => {
      const errorMessage: ChatMessage = {
        id: this.generateMessageId(),
        type: 'error',
        content: 'I apologize, but I\'m having trouble connecting right now. Please try again in a moment.',
        timestamp: new Date()
      };
      this.addMessage(errorMessage);
      this.loadingSubject.next(false);

    });
  }

  /**
   * Add a message to the chat history
   * @param message - The message to add
   */
  private addMessage(message: ChatMessage): void {
    const currentMessages = this.messagesSubject.value;
    const updatedMessages = [...currentMessages, message];

    // Limit message history to prevent memory issues
    if (updatedMessages.length > this.MAX_MESSAGES) {
      updatedMessages.shift(); // Remove oldest message
    }

    this.messagesSubject.next(updatedMessages);
  }

  /**
   * Add a system message
   * @param content - Message content
   */
  private addSystemMessage(content: string): void {
    const message: ChatMessage = {
      id: this.generateMessageId(),
      type: 'system',
      content,
      timestamp: new Date()
    };
    this.addMessage(message);
  }

  /**
   * Generate a unique message ID
   */
  private generateMessageId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private readonly SCREEN_PROMPTS: Record<string, SuggestedPrompt[]> = {
    'program-dashboard': [
      { text: 'Summarize current census', task: 'summarize_current_census' },
      { text: 'Generate Weekly Operational Report', task: 'weekly_operational_report' },
    ],

    'app-enrolled-patient': [
      { text: 'Create Care Plan', task: 'careplan' },
      { text: 'Generate Risk Overview', task: 'generate_risk_overview' },
      { text: 'Draft Escalation Report', task: 'draft_escalation_report' },
    ],

    // ── All other screens (fallback) ──────────────────────────────────────
    'general': [
      { text: 'What can you help me with?', task: 'help' },
    ],
  };

  private updateSuggestedPrompts(context: AppContext): void {
    // 1. Check active_tab first — most specific, set by individual components
    if (context.active_tab && this.SCREEN_PROMPTS[context.active_tab]) {
      this.suggestedPromptsSubject.next(this.SCREEN_PROMPTS[context.active_tab]);
      return;
    }

    // 2. Check screen_name — derived automatically from the route URL
    if (context.screen_name && this.SCREEN_PROMPTS[context.screen_name]) {
      this.suggestedPromptsSubject.next(this.SCREEN_PROMPTS[context.screen_name]);
      return;
    }

    // 3. Fall back to general prompts
    this.suggestedPromptsSubject.next(this.SCREEN_PROMPTS['general']);
  }

  public getSuggestedPrompts(): SuggestedPrompt[] {
    return this.suggestedPromptsSubject.value;
  }

  public getMessages(): ChatMessage[] {
    return this.messagesSubject.value;
  }

  /**
   * Clear all chat messages
   */
  public clearHistory(): void {
    this.messagesSubject.next([]);
    this.addSystemMessage('How can I help you?');
  }

  /**
   * Check if chat is currently loading
   * @returns Loading state
   */
  public isLoading(): boolean {
    return this.loadingSubject.value;
  }
}
